import React from 'react';
import { Link } from 'react-router-dom';
import {
    Plane,
    Shield,
    Radio,
    Radar,
    Server,
    Satellite,
    ArrowRight,
    CheckCircle2,
    Cloud,
    Zap,
    Clock,
    Target
} from 'lucide-react';
import PageLayout from '../../Layout/PageLayout';
import PageHeader from '../../PageHeader/PageHeader';
import ModernCTA from '../../CTA/ModernCTA';
import './ModernSolutions.scss';

const ModernAirForce = () => {
    const products = [
        {
            icon: <Radar size={28} />,
            title: 'Air Defense Systems',
            description: 'Integrated radar and detection systems for airspace monitoring.',
            badge: 'Combat-ready',
            features: ['Multi-spectrum radar', 'Threat identification', 'Early warning', 'Target tracking']
        },
        {
            icon: <Radio size={28} />,
            title: 'Aviation Communications',
            description: 'Secure air-to-ground and air-to-air communication systems.',
            badge: 'Flight-certified',
            features: ['Encrypted voice', 'Data link', 'SATCOM backup', 'Jam-resistant']
        },
        {
            icon: <Server size={28} />,
            title: 'Base Operations Technology',
            description: 'Complete IT infrastructure for Air Force installations.',
            badge: 'Enterprise',
            features: ['Network infrastructure', 'Data centers', 'End-user computing', 'Cybersecurity']
        },
        {
            icon: <Cloud size={28} />,
            title: 'Cloud & Edge Computing',
            description: 'Distributed computing solutions for tactical and strategic operations.',
            badge: 'IL5 Certified',
            features: ['Tactical edge', 'Cloud migration', 'Hybrid solutions', 'Data analytics']
        }
    ];

    const capabilities = [
        {
            icon: <Plane size={28} />,
            metric: '100+',
            title: 'Bases Supported',
            description: 'Air Force installations worldwide'
        },
        {
            icon: <Shield size={28} />,
            metric: 'Zero',
            title: 'Security Breaches',
            description: 'Perfect security track record'
        },
        {
            icon: <Zap size={28} />,
            metric: '<1hr',
            title: 'Response Time',
            description: 'Critical incident response'
        },
        {
            icon: <Clock size={28} />,
            metric: '99.99%',
            title: 'System Availability',
            description: 'Mission-critical uptime'
        }
    ];

    const features = [
        { icon: <Plane size={24} />, title: 'Aviation', text: 'Flight-ready systems' },
        { icon: <Shield size={24} />, title: 'Secure', text: 'Military encryption' },
        { icon: <Satellite size={24} />, title: 'Connected', text: 'Global reach' },
        { icon: <Target size={24} />, title: 'Precise', text: 'Mission accuracy' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Air Force Solutions"
                subtitle="Technology for air dominance"
                breadcrumbs={[
                    { label: 'Industries', path: '/serve' },
                    { label: 'Department of Defense', path: '/department' },
                    { label: 'Air Force Solutions' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-solutions-hero">
                <div className="jk-solutions-hero__container">
                    <div className="jk-solutions-hero__grid jk-military-hero__grid">
                        <div className="jk-solutions-hero__content">
                            <span className="jk-section-label">
                                <Plane size={16} />
                                U.S. Air Force Technology Partner
                            </span>
                            <h2 className="jk-solutions-hero__title">
                                Advanced Technology for
                                <span className="highlight"> Air Superiority</span>
                            </h2>
                            <p className="jk-solutions-hero__description">
                                We deliver cutting-edge technology solutions for Air Force operations.
                                From air defense systems to base infrastructure, our solutions enable
                                air dominance and mission success across all operational domains.
                            </p>
                            <div className="jk-solutions-hero__buttons">
                                <Link to="/contact" className="jk-btn jk-btn--primary">
                                    Deploy Solutions
                                    <ArrowRight size={20} />
                                </Link>
                                <Link to="/department" className="jk-btn jk-btn--secondary">
                                    DoD Overview
                                </Link>
                            </div>
                        </div>

                        <div className="jk-solutions-hero__features">
                            {features.map((feature, index) => (
                                <div key={index} className="jk-solutions-hero__feature-card">
                                    <div className="jk-solutions-hero__feature-icon">
                                        {feature.icon}
                                    </div>
                                    <h4 className="jk-solutions-hero__feature-title">{feature.title}</h4>
                                    <p className="jk-solutions-hero__feature-text">{feature.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Section */}
            <section className="jk-solutions-services">
                <div className="jk-solutions-services__container">
                    <div className="jk-solutions-services__header">
                        <span className="jk-section-label">
                            <Server size={16} />
                            Air Force Solutions
                        </span>
                        <h2 className="jk-solutions-services__title">
                            Mission-Ready
                            <span className="highlight"> Technology</span>
                        </h2>
                        <p className="jk-solutions-services__subtitle">
                            Systems designed for Air Force operational excellence
                        </p>
                    </div>

                    <div className="jk-solutions-services__grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
                        {products.map((product, index) => (
                            <div key={index} className="jk-solutions-services__card">
                                <div className="jk-solutions-services__card-header">
                                    <div className="jk-solutions-services__card-icon">
                                        {product.icon}
                                    </div>
                                    <span className="jk-solutions-services__card-badge">{product.badge}</span>
                                </div>
                                <h3 className="jk-solutions-services__card-title">{product.title}</h3>
                                <p className="jk-solutions-services__card-description">{product.description}</p>
                                <ul className="jk-solutions-services__card-features">
                                    {product.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={12} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="jk-solutions-capabilities">
                <div className="jk-solutions-capabilities__container">
                    <div className="jk-solutions-capabilities__header">
                        <h2 className="jk-solutions-capabilities__title">
                            Operational
                            <span className="highlight"> Excellence</span>
                        </h2>
                    </div>

                    <div className="jk-solutions-capabilities__grid">
                        {capabilities.map((capability, index) => (
                            <div key={index} className="jk-solutions-capabilities__card">
                                <div className="jk-solutions-capabilities__card-icon">
                                    {capability.icon}
                                </div>
                                <div className="jk-solutions-capabilities__card-metric">{capability.metric}</div>
                                <h3 className="jk-solutions-capabilities__card-title">{capability.title}</h3>
                                <p className="jk-solutions-capabilities__card-text">{capability.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernAirForce;
